import React from "react";
import { ReactSVG } from "react-svg";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";

// region Libraries
import { Tooltip } from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, TimelineVehicleStatesMessages, VehiclesStatesTypesMessages } from "@shared/languages/interfaces";
// region Constants
import { VehicleStatesTypesID } from "@shared/constants/vehicle-states-types.enum";
import { AlertTypesID } from "@shared/constants/alert-types.enum";
// endregion Constants
// region Interfaces
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { VehicleState } from "@shared/interfaces/vehicle-state.interface";
import { Duration } from "@shared/interfaces/duration.interface";
// endregion Interfaces
// region Assets
import LockIcon from "@assets/icons/lockIcon.svg";
// endregion Assets
// region Utils
import { formatOdometer } from "@shared/utils/useful-functions";
import utils from "../../../utils/useful-functions";
import { getDischargeBreakdown } from "./discharge-breakdown";
// endregion Utils
// region Styles
import { Container, DischargeBreakdownTooltipStyle } from "./styles";
// endregion Styles

type TimeLineVehicleStatesProps = { vehicle: Vehicle,
  historicalTravel?: boolean,
  showDate?: boolean,
  postUnloadIdleTime?: Duration
};
const TimeLineVehicleStates: React.FC<TimeLineVehicleStatesProps> = ({ vehicle, historicalTravel, postUnloadIdleTime }) => {

  const blockAlerts = vehicle?.alerts?.filter((alert) => alert.type.id_alert_type === AlertTypesID.BLOQUEIO) ?? [];
  const lastBlockAlert = blockAlerts[blockAlerts.length - 1];

  const sortedStates = vehicle.states.sort((a, b) => new Date(a.registration_date).getTime() - new Date(b.registration_date).getTime());

  const { t } = useTranslation();

  const formatSeconds = (seconds: number) => utils.formatDateIfHave(utils.calcDataRangeFromSeconds(seconds), "durationDescriptiveTime");

  /**
   * Sub-item under the "Descarregando" card: splits the state into effective unloading and post-unload idleness.
   * The state itself (start, end, duration) is untouched; this only reads it.
   * @param state The "Descarregando" state
   */
  const renderDischargeBreakdown = (state: VehicleState) => {

    const breakdown = getDischargeBreakdown(state.registration_date, state.finish_date, postUnloadIdleTime);

    if (!breakdown) return null;

    const unloadingPercent = breakdown.totalSeconds > 0 ? (breakdown.unloadingSeconds / breakdown.totalSeconds) * 100 : 0;

    return (
      <div className="discharge-breakdown" data-testid="discharge-breakdown">
        <span className="discharge-breakdown-dot" />
        <div className="discharge-breakdown-content">
          <div className="discharge-breakdown-title">
            {t(TimelineVehicleStatesMessages.unloadBreakdownTitle)}
            <Tooltip
              title={t(TimelineVehicleStatesMessages.unloadBreakdownHelp)}
              placement="top"
              arrow
              classes={{ popper: "discharge-breakdown-tooltip" }}
            >
              <HelpOutlineIcon className="discharge-breakdown-help" aria-label={t(TimelineVehicleStatesMessages.unloadBreakdownHelp)} />
            </Tooltip>
          </div>
          <div className="discharge-breakdown-bar">
            <span className="unloading" style={{ width: `${unloadingPercent}%` }} />
            <span className="idle" style={{ width: `${100 - unloadingPercent}%` }} />
          </div>
          <div className="discharge-breakdown-row">
            <span className="swatch unloading" />
            <span className="label">{t(TimelineVehicleStatesMessages.effectiveUnloadTime)}</span>
            <span className="value">{formatSeconds(breakdown.unloadingSeconds)}</span>
          </div>
          <div className="discharge-breakdown-row">
            <span className="swatch idle" />
            <span className="label">{t(TimelineVehicleStatesMessages.postUnloadIdleTime)}</span>
            <span className="value">{formatSeconds(breakdown.idleSeconds)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Container>
      <DischargeBreakdownTooltipStyle />
      <div className="title">
        {t(TimelineVehicleStatesMessages.statesTitle)}
      </div>
      <VerticalTimeline layout="1-column" chi>
        {vehicle.states ? (
          sortedStates.map((state, index, statesArray) => (
            <React.Fragment key={state.id_vehicle_state}>
              <VerticalTimelineElement
                contentStyle={{ background: "rgb(221 221 221 / 80%)", color: "black" }}
                contentArrowStyle={{ borderRight: "7px solid  rgb(221 221 221 / 80%)" }}
                icon={utils.getStateIcon(state.status.id_vehicle_state_type)}
              >
                <div className="block flex">
                  <div
                    className="duration-title"
                    style={{
                      color: index === vehicle.states.length - 1
                        ? "red"
                        : "black"
                    }}
                  >
                    {t(VehiclesStatesTypesMessages[state.status.id_vehicle_state_type])}
                  </div>

                  {(() => {
                    let show = true;

                    if (historicalTravel && statesArray.length - 1 === index) {
                      show = false;
                    }
                    if (show) {
                      return (
                        <div className="odometer">
                          {formatOdometer(((state?.finish_odometer === 0
                            ? vehicle.current_odometer
                            : state.finish_odometer) || 0) - state.start_odometer)}
                        </div>
                      );
                    }

                    return <></>;
                  })()}

                </div>
                {index !== 0 && index !== vehicle.states.length - 1 && state.status.id_vehicle_state_type
                  !== VehicleStatesTypesID.EM_OBRA
                  && state.status.id_vehicle_state_type !== VehicleStatesTypesID.DESCARREGANDO && (
                  <div className="block">
                    {state.location && (
                      state.location.name
                        || (
                          <>
                            {state.location.address}, {state.location.number || "S/N"} - {state.location.district}
                          </>
                        )
                    )}
                  </div>
                )}
                <div className="block">
                  <div className="content">
                    <div className="subtitle">
                      {t(GlobalMessages.start)}
                    </div>
                    <div className="text">{utils.formatDateIfHave(state.registration_date, "fullDateWithoutYear")}</div>
                  </div>
                  {state.finish_date && (
                    <div className="content">
                      <div className="subtitle">{t(GlobalMessages.end)}</div>
                      <div className="text">
                        {utils.formatDateIfHave(state.finish_date, "fullDateWithoutYear")}
                      </div>
                    </div>
                  )}
                  <div className="content">
                    {(state.duration || !historicalTravel)
                      && (
                        <>
                          <div className="subtitle">{t(GlobalMessages.duration)}</div>
                          <div className="text" style={{ display: "flex", alignItems: "center" }}>
                            {
                              state.duration
                                ? utils.formatDateIfHave(state.duration, "durationDescriptiveTime")
                                : utils.formatDateIfHave(utils.calcDataRange(state.registration_date, new Date()),
                                  "durationDescriptiveTime")
                            }
                          </div>
                        </>
                      )}
                  </div>
                  {state.mixer_pressure && (
                    <div className="content">
                      <>
                        <div className="subtitle">{t(TimelineVehicleStatesMessages.manometer)}</div>
                        <div className="text" style={{ display: "flex", alignItems: "center" }}>
                          {state.mixer_pressure.toString().replace(".", ",")} bar
                        </div>
                      </>
                    </div>
                  )}
                </div>
              </VerticalTimelineElement>
              {state.status.id_vehicle_state_type === VehicleStatesTypesID.DESCARREGANDO && renderDischargeBreakdown(state)}
            </React.Fragment>
          ))
        ) : (
          <></>
        )}
        {vehicle.blocked && (
          <VerticalTimelineElement
            contentStyle={{ background: "#F00", color: "white" }}
            contentArrowStyle={{ borderRight: "7px solid red" }}
            icon={<ReactSVG src={LockIcon} />}
          >
            <div className="block flex">
              <div
                className="duration-title"
                style={{ color: "white", fontWeight: "bold" }}
              >
                {t(TimelineVehicleStatesMessages.blockedVehicle)}
              </div>
            </div>
            <div className="block">
              <div className="content">
                <div className="subtitle">
                  {t(TimelineVehicleStatesMessages.blockingDate)}
                </div>
                <div className="text">
                  {utils.formatDateIfHave(lastBlockAlert?.occurrence_date, "fullDateWithoutYear")}
                </div>
              </div>
              <div className="content">
                <div className="subtitle">{t(GlobalMessages.duration)}</div>
                <div className="text" style={{ display: "flex", alignItems: "center" }}>
                  {utils.formatDateIfHave(utils.calcDataRange(lastBlockAlert?.occurrence_date, new Date()),
                    "durationDescriptiveTime")}
                  <AccessTimeIcon style={{ height: "18px" }} />
                </div>
              </div>
            </div>
          </VerticalTimelineElement>
        )}
      </VerticalTimeline>
    </Container>
  );
};

TimeLineVehicleStates.defaultProps = {
  showDate: true
};

export default TimeLineVehicleStates;
